/*global payhere*/
import React from "react";
import { useNavigate } from "react-router-dom";
import { PAYHERE_RECURRING_ENABLED } from "../../config/payhere";

/**
 * @param {{ formData: any, label?: string, forceOneTime?: boolean, forceRecurring?: boolean, variant?: "primary" | "outline" }} props
 */
const PaymentButton = ({
  formData,
  label = undefined,
  forceOneTime = false,
  forceRecurring = false,
  variant = "primary",
}) => {
  const navigate = useNavigate();
  const useRecurring =
    forceRecurring || (PAYHERE_RECURRING_ENABLED && !forceOneTime);
  const monthlyPrice = parseFloat(formData?.monthlyPrice ?? formData?.price ?? 0);
  const pendingMonths = Number(formData?.pendingMonths ?? 0);
  const billingMonths = pendingMonths > 0 ? pendingMonths : 1;
  const oneTimeTotal = (monthlyPrice * billingMonths).toFixed(2);
  const startupFee =
    pendingMonths > 1
      ? (monthlyPrice * (pendingMonths - 1)).toFixed(2)
      : undefined;

  const paymentDetails = {
    order_id: formData?.orderId,
    amount: useRecurring
      ? monthlyPrice.toFixed(2)
      : oneTimeTotal,
    currency: "LKR",
    first_name: formData?.firstName,
    last_name: formData?.lastName,
    email: formData?.email,
    phone: formData?.phone,
    address: formData?.address,
    clinicid: formData?.clinicid,
    startDate: formData?.startDate,
    pendingMonths,
    billingMonths,
    city: "Colombo",
    country: "Sri Lanka",
  };

  const handlePayment = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/client/payhere/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paymentDetails),
        }
      );

      if (response.ok) {
        const { hash, merchant_id } = await response.json();
        const payment = {
          merchant_id: merchant_id,
          return_url: undefined,
          cancel_url: undefined,
          notify_url: `${process.env.REACT_APP_API}/client/payhere/notify`,
          order_id: paymentDetails.order_id,
          items: "Care32 Dent Monthly Subscription",
          amount: paymentDetails.amount,
          currency: paymentDetails.currency,
          first_name: paymentDetails.first_name,
          last_name: paymentDetails.last_name,
          email: paymentDetails.email,
          phone: paymentDetails.phone,
          address: paymentDetails.address,
          city: paymentDetails.city,
          country: paymentDetails.country,
          hash: hash,
          custom_1: paymentDetails.clinicid,
          custom_2: useRecurring
            ? `${paymentDetails.startDate}%1%${paymentDetails.pendingMonths}`
            : `${paymentDetails.startDate}%${paymentDetails.billingMonths}`,
        };

        if (useRecurring) {
          payment.recurrence = "1 Month";
          payment.duration = "Forever";
          if (startupFee) {
            payment.startup_fee = startupFee;
          }
        }

        if (typeof payhere === "undefined") {
          console.error("PayHere SDK not loaded");
          navigate("/client/payment/error");
          return;
        }

        payhere.onCompleted = function onCompleted() {
          navigate("/client/payment/success");
        };
        payhere.onDismissed = function onDismissed() {
          navigate("/client/payment/cancel");
        };
        payhere.onError = function onError(error) {
          const message = String(error || "");
          if (message.toLowerCase().includes("token")) {
            navigate("/client/payment/error?reason=token");
            return;
          }
          navigate("/client/payment/error");
        };

        payhere.startPayment(payment);
      } else {
        console.error("Failed to generate hash for payment.");
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const buttonClass =
    variant === "outline"
      ? "flex items-center gap-1.5 px-3 py-1.5 text-primary bg-white border border-primary rounded-lg font-semibold hover:bg-primary/5 transition-all duration-200 text-xs h-[32px] dark:bg-slate-800 dark:text-primaryLight dark:border-primaryLight"
      : "flex items-center gap-1.5 px-3 py-1.5 text-white bg-gradient-to-r from-primary to-primaryLight rounded-lg font-semibold hover:shadow-lg transition-all duration-200 shadow-md text-xs h-[32px]";

  return (
    <button onClick={handlePayment} className={buttonClass}>
      {label ?? (useRecurring ? "Subscribe" : "Pay now")}
    </button>
  );
};

export default PaymentButton;
